import Bugsnag from "@bugsnag/js";
import type {
  AuthenticationApiFactory,
  TokenPairDto,
} from "@docket/consumer-app-client-typescript-axios";
import { isAxiosError } from "axios";
import { jwtDecode } from "jwt-decode";
import db, { Key } from "../database/database";
import { ApiError } from "./errors";
import type { Tokens, UserInfo } from "../models/Interfaces";

/** Where the provider reads and writes the token pair. */
export interface TokenStore {
  get(): Promise<Tokens | null>;
  set(tokens: Tokens): Promise<void>;
}

/** The encrypted database, under the same key as the React app. */
export const dbTokenStore: TokenStore = {
  async get() {
    let tokens = await db().getItem<Tokens>(Key.Tokens);
    /**
     * Versions before the "tokens" key kept the pair inside the user info. Migrate it on first read
     * (an update without a reload reaches this code before any other migration).
     */
    if (!tokens) {
      const user = await db().getItem<UserInfo & { tokens?: TokenPairDto }>(Key.UserInfo);
      const userTokens = user?.tokens;
      if (userTokens) {
        await db().setItem(Key.Tokens, userTokens);
        tokens = userTokens;
      }
    }
    return tokens;
  },
  async set(tokens) {
    await db().setItem(Key.Tokens, tokens);
  },
};

/** Tokens this close to expiring count as expired, so they're refreshed before the server rejects them. */
export const EXPIRY_LEEWAY_MS = 60_000;

export function tokenExpired(token: string, now = Date.now()): boolean {
  if (!token) {
    return true;
  }
  const claims = jwtDecode(token);
  // exp and nbf are unix epochs in seconds. A missing exp means expired; a missing nbf means valid now.
  const expiration = (claims.exp || 0) * 1000;
  const notBefore = claims.nbf ? claims.nbf * 1000 : now;
  return expiration - EXPIRY_LEEWAY_MS < now || now < notBefore;
}

/**
 * Refresh failures that another attempt can't fix: the refresh token is missing, invalid or from an
 * expired token family (401), or the request itself is wrong.
 */
const NON_RETRYABLE_STATUSES = [401, 400, 403, 404];

/** No tokens are stored: signed out, or never signed in. Another refresh can't change that. */
export class NoTokensError extends Error {
  override readonly name = "NoTokensError";

  constructor() {
    super("Attempted to fetch tokens but the supplier returned none. Was it initialized?");
  }
}

/**
 * The session ended (signed out, or signed in over) before what it asked for was done: a token read,
 * a refresh, or a request's retry after a 401.
 */
export class SessionEndedError extends Error {
  override readonly name = "SessionEndedError";

  constructor(options: { cause?: unknown } = {}) {
    super("The session ended before its request was done", options);
  }
}

type AuthApi = Pick<ReturnType<typeof AuthenticationApiFactory>, "refreshTokens">;

export interface RefreshingTokenProviderOptions {
  /** Called once the tokens can't be refreshed any more; the user has to sign in again. */
  onAuthFailure: () => void;
  maxRetries?: number;
  maxBackoffMs?: number;
}

export class RefreshingTokenProvider {
  #curRefresh: Promise<Tokens> | null = null;
  /** Which session the tokens belong to; reset() moves on to the next. */
  #session = 0;
  readonly #onAuthFailure: () => void;
  readonly #maxRetries: number;
  readonly #maxBackoffMs: number;

  constructor(
    private readonly authApi: AuthApi,
    private readonly store: TokenStore,
    { onAuthFailure, maxRetries = 30, maxBackoffMs = 60_000 }: RefreshingTokenProviderOptions,
  ) {
    this.#onAuthFailure = onAuthFailure;
    this.#maxRetries = maxRetries;
    this.#maxBackoffMs = maxBackoffMs;
  }

  /**
   * The current tokens, refreshed first if the access token is expired or about to be. Only the
   * session's own: one that ends while they're read or refreshed gets none.
   */
  async tokens(): Promise<Tokens> {
    const session = this.#session;
    let tokens = await this.storedTokens();
    this.assertSession(session);
    if (tokenExpired(tokens.access)) {
      tokens = await this.refreshTokens();
      this.assertSession(session);
    }
    return tokens;
  }

  /** Refresh the tokens. Concurrent callers share one refresh, including its retries. */
  refreshTokens(): Promise<Tokens> {
    if (!this.#curRefresh) {
      const refresh: Promise<Tokens> = this.refreshWithRetries(0, this.#session).finally(() => {
        // After a reset() this is another session's refresh, which isn't this one's to clear.
        if (this.#curRefresh === refresh) {
          this.#curRefresh = null;
        }
      });
      this.#curRefresh = refresh;
    }
    return this.#curRefresh;
  }

  /** The session requests are made in now; a request made in an earlier one isn't sent again. */
  get session(): number {
    return this.#session;
  }

  /**
   * The session is ending: a refresh still running for it stops, and keeps nothing it gets, so it
   * can't hand its tokens to the next session or take the next session's.
   */
  reset(): void {
    this.#session++;
    this.#curRefresh = null;
  }

  private async storedTokens(): Promise<Tokens> {
    const tokens = await this.store.get();
    if (!tokens) {
      const err = new NoTokensError();
      Bugsnag.notify(err);
      throw err;
    }
    return tokens;
  }

  private async refreshWithRetries(refreshAttempt: number, session: number): Promise<Tokens> {
    const sleepTime = this.calculateBackoff(refreshAttempt);
    if (sleepTime > 0) {
      await new Promise((resolve) => setTimeout(resolve, sleepTime));
    }

    try {
      // Checked after every wait: the backoff, the store (which may hold the next session's tokens by
      // then), the server and the save.
      this.assertSession(session);
      const { refresh } = await this.storedTokens();
      this.assertSession(session);
      const updatedTokens = await this.authApi.refreshTokens({ refreshToken: refresh });
      this.assertSession(session);
      await this.store.set(updatedTokens.data);
      this.assertSession(session);
      return updatedTokens.data;
    } catch (err) {
      // Neither is fixed by trying again, and neither means the user's tokens were turned down.
      if (err instanceof NoTokensError || err instanceof SessionEndedError) {
        throw err;
      }
      // An ended session's refresh fails as that, whatever went wrong: above all, it can't report an
      // auth failure, which would sign out whoever is signed in now.
      this.assertSession(session, err);
      // The auth API's own errors arrive as ApiErrors (see client.ts); raw axios errors are accepted too.
      const status =
        err instanceof ApiError ? err.status : isAxiosError(err) ? err.response?.status : undefined;
      if (status !== undefined && NON_RETRYABLE_STATUSES.includes(status)) {
        this.#onAuthFailure();
        throw new Error(`Non-retryable error while refreshing tokens: ${(err as Error).message}`, {
          cause: err,
        });
      }
      if (refreshAttempt >= this.#maxRetries - 1) {
        throw new Error(String(err), { cause: err });
      }
      return this.refreshWithRetries(refreshAttempt + 1, session);
    }
  }

  private assertSession(session: number, cause?: unknown): void {
    if (session !== this.#session) {
      throw new SessionEndedError({ cause });
    }
  }

  private calculateBackoff(refreshAttempt: number): number {
    // Math.pow won't overflow; it'll go to Infinity instead.
    return refreshAttempt === 0
      ? 0
      : Math.min(this.#maxBackoffMs, Math.pow(2, refreshAttempt) * 10) + Math.random() * 10;
  }
}
